import { useState, useEffect } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { stockAPI } from '../../../api';
import Layout from '../../../components/Layout';
import { Button } from '../../../components/ui/Button';
import { Input } from '../../../components/ui/Input';
import { Card, CardContent } from '../../../components/ui/Card';
import { FormLabel } from '../../../components/ui/FormLabel';
import { Table, TableHeader, TableBody, TableRow, TableHead, TableCell } from '../../../components/ui/Table';
import { Eye, AlertTriangle } from 'lucide-react';
import { STOCK_CATEGORIES, labelFor, formatQuantity } from '../../../lib/stockConstants';

const selectClass =
  'flex h-9 w-full border border-gray-300 dark:border-gray-600 rounded-md bg-white dark:bg-gray-700 text-gray-900 dark:text-gray-100 px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-blue-500';

const StockList = () => {
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [search, setSearch] = useState('');
  const [categoryFilter, setCategoryFilter] = useState('');
  const [lowStockOnly, setLowStockOnly] = useState(searchParams.get('lowStock') === 'true');

  useEffect(() => {
    if (searchParams.get('lowStock') === 'true') {
      setLowStockOnly(true);
    }
  }, [searchParams]);
  const [pagination, setPagination] = useState({ page: 1, limit: 20, total: 0 });

  useEffect(() => {
    fetchItems();
  }, [pagination.page, search, categoryFilter, lowStockOnly]);

  const fetchItems = async () => {
    try {
      setLoading(true);
      setError('');
      const response = await stockAPI.getAll({
        page: pagination.page,
        limit: pagination.limit,
        search: search || undefined,
        category: categoryFilter || undefined,
        lowStockOnly: lowStockOnly ? 'true' : undefined,
      });
      setItems(response.items || []);
      setPagination((prev) => ({
        ...prev,
        total: response.pagination?.total ?? 0,
      }));
    } catch (err) {
      setError(err.message || 'Failed to load stock items');
    } finally {
      setLoading(false);
    }
  };

  const totalPages = Math.max(1, Math.ceil(pagination.total / pagination.limit));

  return (
    <Layout>
      <div className="bg-[var(--ds-surface-panel)] border-b border-[var(--ds-border-subtle)] shadow-[var(--ds-shadow-card)]">
        <div className="max-w-7xl mx-auto px-6 py-6">
          <div className="md:flex md:items-center md:justify-between">
            <div>
              <h1 className="text-display text-[var(--ds-text-primary)] font-bold">Stock</h1>
              <p className="mt-1 text-meta text-[var(--ds-text-secondary)]">
                Consumables and materials inventory
              </p>
            </div>
            <div className="mt-4 flex md:mt-0 gap-3">
              <Button variant="outline" size="sm" onClick={() => navigate('/tenant/assets')}>
                Asset register
              </Button>
              <Button variant="outline" size="sm" onClick={() => navigate('/tenant/dashboard')}>
                Dashboard
              </Button>
              <Button variant="primary" onClick={() => navigate('/tenant/stock/new')}>
                + Add stock item
              </Button>
            </div>
          </div>
        </div>
      </div>

      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
        <Card className="mb-6 bg-white dark:bg-gray-800">
          <CardContent className="p-4">
            <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
              <div className="md:col-span-2">
                <FormLabel className="text-gray-700 dark:text-gray-300">Search</FormLabel>
                <Input
                  type="text"
                  placeholder="Name, SKU, supplier..."
                  value={search}
                  onChange={(e) => {
                    setSearch(e.target.value);
                    setPagination((p) => ({ ...p, page: 1 }));
                  }}
                  className="border-gray-300 dark:border-gray-600 bg-white dark:bg-gray-700 text-gray-900 dark:text-gray-100"
                />
              </div>
              <div>
                <FormLabel className="text-gray-700 dark:text-gray-300">Category</FormLabel>
                <select
                  value={categoryFilter}
                  onChange={(e) => {
                    setCategoryFilter(e.target.value);
                    setPagination((p) => ({ ...p, page: 1 }));
                  }}
                  className={selectClass}
                >
                  <option value="">All categories</option>
                  {STOCK_CATEGORIES.map((c) => (
                    <option key={c.value} value={c.value}>
                      {c.label}
                    </option>
                  ))}
                </select>
              </div>
              <div className="flex items-end">
                <label className="flex items-center gap-2 text-sm text-gray-700 dark:text-gray-300 cursor-pointer">
                  <input
                    type="checkbox"
                    checked={lowStockOnly}
                    onChange={(e) => {
                      setLowStockOnly(e.target.checked);
                      setPagination((p) => ({ ...p, page: 1 }));
                    }}
                    className="rounded border-gray-300 dark:border-gray-600"
                  />
                  Low stock only
                </label>
              </div>
            </div>
          </CardContent>
        </Card>

        {error && (
          <div className="mb-4 p-4 rounded-md bg-red-50 dark:bg-red-900/20 text-red-700 dark:text-red-300 text-sm">
            {error}
          </div>
        )}

        <Card className="bg-white dark:bg-gray-800">
          <CardContent className="p-0">
            {loading ? (
              <p className="p-6 text-gray-600 dark:text-gray-400">Loading stock...</p>
            ) : items.length === 0 ? (
              <p className="p-6 text-gray-600 dark:text-gray-400">No stock items found.</p>
            ) : (
              <Table>
                <TableHeader>
                  <TableRow>
                    <TableHead>Item</TableHead>
                    <TableHead>SKU</TableHead>
                    <TableHead>Category</TableHead>
                    <TableHead>On hand</TableHead>
                    <TableHead>Min level</TableHead>
                    <TableHead>Location</TableHead>
                    <TableHead className="text-right">Actions</TableHead>
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {items.map((item) => (
                    <TableRow
                      key={item.id}
                      className={item.isLowStock ? 'bg-amber-50/50 dark:bg-amber-900/10' : ''}
                    >
                      <TableCell>
                        <div className="flex items-center gap-2">
                          {item.isLowStock && (
                            <AlertTriangle className="w-4 h-4 text-amber-500 shrink-0" />
                          )}
                          <span className="font-medium text-gray-900 dark:text-gray-100">
                            {item.itemName}
                          </span>
                        </div>
                      </TableCell>
                      <TableCell className="text-gray-600 dark:text-gray-400">
                        {item.sku || '—'}
                      </TableCell>
                      <TableCell>{labelFor(STOCK_CATEGORIES, item.category)}</TableCell>
                      <TableCell>{formatQuantity(item.quantityOnHand, item.unitType)}</TableCell>
                      <TableCell>
                        {item.minimumStockLevel != null
                          ? formatQuantity(item.minimumStockLevel, item.unitType)
                          : '—'}
                      </TableCell>
                      <TableCell className="text-gray-600 dark:text-gray-400">
                        {item.storageLocation || '—'}
                      </TableCell>
                      <TableCell className="text-right">
                        <Button
                          variant="ghost"
                          size="sm"
                          onClick={() => navigate(`/tenant/stock/${item.id}`)}
                        >
                          <Eye className="w-4 h-4" />
                        </Button>
                      </TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            )}
          </CardContent>
        </Card>

        {totalPages > 1 && (
          <div className="mt-4 flex justify-center gap-2">
            <Button
              variant="outline"
              size="sm"
              disabled={pagination.page <= 1}
              onClick={() => setPagination((p) => ({ ...p, page: p.page - 1 }))}
            >
              Previous
            </Button>
            <span className="text-sm text-gray-600 dark:text-gray-400 self-center">
              Page {pagination.page} of {totalPages}
            </span>
            <Button
              variant="outline"
              size="sm"
              disabled={pagination.page >= totalPages}
              onClick={() => setPagination((p) => ({ ...p, page: p.page + 1 }))}
            >
              Next
            </Button>
          </div>
        )}
      </div>
    </Layout>
  );
};

export default StockList;
